/**
 * The one renderer for a workflow's `input_schema` — the flat
 * `{name, type, description, optional, options}` spec authored in InputContractDrawer and
 * enforced (partially) by services/workflows/inputSchema.js.
 *
 * Extracted from TestDialog.jsx so the API tab's Interactive Console renders the SAME
 * controls as the Test dialog. This repo already has four spec-driven form renderers
 * (TestDialog, ToolArgumentsForm, ConnectionDetail + lib/fieldVisibility, toolProviderShared),
 * each for a genuinely different spec shape; a fifth for a shape that already has one would
 * be the drift this extraction exists to prevent.
 *
 * Scope note, deliberately narrow: the authorable input types are string | number | boolean
 * only (services/workflows/llmAppSpec.js's INPUT_TYPES, scalar-on-purpose). Anything richer
 * belongs in the raw JSON editor, not here.
 */

const inputClass = 'w-full border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]';

/** Values arrive as strings from the DOM; the engine hands the input to expressions and
 *  templates verbatim, so `score > 5` needs a real number, not "5". */
export function coerce(value, type) {
  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  if (type === 'boolean') return value === true || value === 'true';
  return value;
}

/** An options field is a fixed choice list — `validateRunInputOptions` 400s on anything
 *  outside it — so it always has a value (the select falls back to the first option) and can
 *  never be "missing" the way an untouched text field can. */
export function isOptionsField(f) {
  return f.type === 'string' && Array.isArray(f.options) && f.options.length > 0;
}

/** Rows with no usable name are authoring debris, not inputs. */
export function namedFields(fields) {
  return (fields || []).filter((f) => f && f.name && f.name.trim());
}

/** Required fields the user has not filled. Shared so the Test dialog and the console agree
 *  on what "incomplete" means — note the server does NOT enforce required-ness
 *  (services/workflows/inputSchema.js: "types and required-ness stay the caller's
 *  business"), so this is a helpful prompt, never a guarantee. */
export function missingRequired(fields, values) {
  return namedFields(fields).filter(
    (f) => !isOptionsField(f) && !f.optional && (values[f.name] === undefined || values[f.name] === ''),
  );
}

/**
 * Form values → the `input` object to send.
 *
 * Dropping `''`/`undefined` is a SUBMIT-time rule, not a per-keystroke one: deleting the key
 * as someone clears a field makes the JSON jump around under them in the console's live
 * preview. Callers that write continuously should pass `{ keepEmpty: true }`.
 */
export function valuesToInput(fields, values, { keepEmpty = false } = {}) {
  const input = {};
  for (const f of namedFields(fields)) {
    // An options field always resolves — the select shows its first option, so the payload
    // must say so too, or the form and the request disagree.
    if (isOptionsField(f)) {
      input[f.name] = coerce(values[f.name] ?? f.options[0], f.type);
      continue;
    }
    const raw = values[f.name];
    // UNTOUCHED (`undefined`) is never materialised, even with keepEmpty. Coercing it would
    // invent a value — a boolean the user never ticked would land in the payload as `false`
    // and `{{input.flag}}` would render "false" instead of empty. keepEmpty is only about
    // holding on to a value the user explicitly CLEARED ('').
    if (raw === undefined) continue;
    if (keepEmpty || raw !== '') input[f.name] = coerce(raw, f.type);
  }
  return input;
}

function FieldLabel({ field }) {
  return (
    <span className="text-xs font-medium text-secondary-500">
      {field.name}{field.optional ? '' : ' *'}
      {field.description ? ` — ${field.description}` : ''}
    </span>
  );
}

export function FieldRow({ field, value, onChange }) {
  if (isOptionsField(field)) {
    return (
      <label className="block space-y-1">
        <FieldLabel field={field} />
        <select className={inputClass} value={value ?? field.options[0]} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value === true || value === 'true'} onChange={(e) => onChange(e.target.checked)} />
        <span>{field.name}{field.optional ? '' : ' *'}</span>
        {field.description && <span className="text-xs text-secondary-500">— {field.description}</span>}
      </label>
    );
  }
  return (
    <label className="block space-y-1">
      <FieldLabel field={field} />
      <input
        className={inputClass}
        type={field.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** The field list itself, with no chrome — the dialog and the console wrap it differently. */
export default function InputSchemaForm({ fields = [], values = {}, onChange, emptyNote }) {
  const named = namedFields(fields);
  if (!named.length) {
    return <p className="text-sm text-secondary-500">{emptyNote || 'This workflow has no input fields, so it runs with an empty input.'}</p>;
  }
  return (
    <div className="space-y-3">
      {named.map((f) => (
        <FieldRow
          key={f.name}
          field={f}
          value={values[f.name]}
          onChange={(v) => onChange(f.name, v)}
        />
      ))}
    </div>
  );
}
